import type { GiftItem, GiftItemPayload, GiftKind, GiftPriority, PublicGiftItem } from '../types/GiftRegistry';

/**
 * Pure helpers of the Gift Registry: labels, record numbers and addresses, the item form (validation and the payload sent to the API),
 * and the guest-page address. Nothing here touches the network.
 */

export const GIFT_KIND_LABELS: Record<GiftKind, string> = { gift: 'Gift', request: 'Request' };
export const GIFT_KIND_OPTIONS = (Object.keys(GIFT_KIND_LABELS) as GiftKind[]).map(value => ({ value, label: GIFT_KIND_LABELS[value] }));
export const GIFT_PRIORITY_LABELS: Record<GiftPriority, string> = { high: 'High', normal: 'Normal', low: 'Low' };
export const GIFT_PRIORITY_OPTIONS = (['high', 'normal', 'low'] as GiftPriority[]).map(value => ({ value, label: GIFT_PRIORITY_LABELS[value] }));

export const GIFT_LIST_PATH = '/gift-registry';
export const GIFT_NEW_PATH = '/gift-registry/new';

/** The record number as shown everywhere: GR-007. */
export const giftRefLabel = (n: number): string => `GR-${String(n).padStart(3, '0')}`;

/** The record page address, with the plain number: /gift-registry/GR-7 (the standard of docs/plans/2026-10-29-url-audit.md). */
export const giftItemPath = (n: number): string => `${GIFT_LIST_PATH}/GR-${n}`;

/** 7 from GR-7 or GR-007; null for anything else. */
export function parseGiftRef(segment: string | undefined | null): number | null {
  const m = /^GR-(\d{1,7})$/i.exec((segment ?? '').trim());
  if (!m) return null;
  const n = Number(m[1]);
  return n > 0 ? n : null;
}

/** The record number in an address like /gift-registry/GR-7 (a trailing slash is fine), else null. */
export function giftRefFromPath(pathname: string): number | null {
  const m = /^\/gift-registry\/([^/]+)\/?$/.exec(pathname);
  return m ? parseGiftRef(m[1]) : null;
}

// ── the guest page ──

const PUBLIC_PATH = /^\/registry\/([A-Za-z0-9_-]{32,64})\/?$/;

/** The share token when `pathname` is a guest page address, else null. */
export function matchPublicRegistryPath(pathname: string): string | null {
  const m = PUBLIC_PATH.exec(pathname);
  return m ? m[1] : null;
}

/** The link the honorees give their guests. */
export const shareUrl = (token: string, origin: string = window.location.origin): string => `${origin}/registry/${token}`;

// ── labels ──

/** "2 of 3 reserved", "Not reserved yet", "All reserved". */
export function reservedSummary(item: Pick<GiftItem, 'quantity_wanted' | 'reserved_quantity'>): string {
  if (item.reserved_quantity <= 0) return 'Not reserved yet';
  if (item.reserved_quantity >= item.quantity_wanted) return item.quantity_wanted === 1 ? 'Reserved' : 'All reserved';
  return `${item.reserved_quantity} of ${item.quantity_wanted} reserved`;
}

/** What a guest reads on an item: "Still wanted" / "2 still wanted" / "Reserved". */
export function guestStatusText(item: Pick<PublicGiftItem, 'status' | 'quantity_wanted' | 'remaining_quantity'>): string {
  if (item.status === 'reserved') return 'Reserved';
  return item.quantity_wanted > 1 ? `${item.remaining_quantity} still wanted` : 'Still wanted';
}

/** Whether an item matches the header search: its number, title, description, kind or who reserved it. */
export function giftMatches(item: GiftItem, term: string): boolean {
  if (!term) return true;
  return [
    giftRefLabel(item.ref_number), item.title, item.description, GIFT_KIND_LABELS[item.kind],
    ...item.reservations.map(r => r.guest_name),
  ].some(v => (v ?? '').toLowerCase().includes(term));
}

// ── the item form ──

export interface GiftForm {
  kind: GiftKind;
  title: string;
  description: string;
  link_url: string;
  photo_url: string;
  quantity_wanted: string;
  priority: GiftPriority;
  price: string;
}

export const emptyGiftForm = (): GiftForm => ({
  kind: 'gift', title: '', description: '', link_url: '', photo_url: '', quantity_wanted: '1', priority: 'normal', price: '',
});

export const giftFormFromItem = (item: GiftItem): GiftForm => ({
  kind: item.kind, title: item.title, description: item.description ?? '', link_url: item.link_url ?? '', photo_url: item.photo_url ?? '',
  quantity_wanted: String(item.quantity_wanted), priority: item.priority, price: item.price ?? '',
});

/** A link typed without its scheme ("shop.example/blender") gets https://. */
export function normaliseLink(raw: string): string {
  const text = raw.trim();
  if (text === '') return '';
  return /^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`;
}

export function isWebLink(text: string): boolean {
  if (!/^https?:\/\/\S+$/i.test(text) || text.length > 2000) return false;
  try {
    const u = new URL(text);
    return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname.includes('.');
  } catch {
    return false;
  }
}

/** "12,500.5" -> "12500.50"; null when it is not an amount with at most 2 decimals. Never a float. */
export function normalisePrice(raw: string): string | null {
  const text = raw.trim().replace(/[\s,]/g, '');
  const m = /^(\d{1,10})(?:\.(\d{1,2}))?$/.exec(text);
  return m ? `${m[1]}.${(m[2] ?? '').padEnd(2, '0')}` : null;
}

export type GiftFormErrors = Partial<Record<keyof GiftForm, string>>;

export function validateGiftForm(form: GiftForm, opts: { canViewFinancials: boolean; reserved?: number }): GiftFormErrors {
  const errors: GiftFormErrors = {};
  if (form.title.trim() === '') errors.title = 'Give the item a name.';
  else if (form.title.trim().length > 200) errors.title = 'Keep the name under 200 characters.';
  if (form.description.length > 2000) errors.description = 'Keep the description under 2,000 characters.';
  const link = normaliseLink(form.link_url);
  if (link !== '' && !isWebLink(link)) errors.link_url = 'Enter a web link, like https://shop.example/item.';
  const photo = form.photo_url.trim();
  if (photo !== '' && !isWebLink(photo) && !/^\/uploads\/[A-Za-z0-9._-]+$/.test(photo)) errors.photo_url = 'Enter a web link to a photo, or upload one.';
  const q = Number(form.quantity_wanted);
  if (!/^\d+$/.test(form.quantity_wanted.trim()) || q < 1 || q > 10000) errors.quantity_wanted = 'Enter a whole number from 1 to 10,000.';
  else if (opts.reserved && q < opts.reserved) errors.quantity_wanted = `${opts.reserved} are already reserved. Remove a reservation first, or keep at least ${opts.reserved}.`;
  if (opts.canViewFinancials && form.price.trim() !== '' && normalisePrice(form.price) === null) errors.price = 'Enter an amount with at most 2 decimals, like 25000 or 19.99.';
  return errors;
}

/** The body for POST / PATCH. A role that may not see money never sends a price. Blank optional fields are sent as null so they clear. */
export function buildGiftPayload(form: GiftForm, opts: { canViewFinancials: boolean }): GiftItemPayload {
  const payload: GiftItemPayload = {
    kind: form.kind,
    title: form.title.trim(),
    description: form.description.trim() === '' ? null : form.description,
    link_url: normaliseLink(form.link_url) || null,
    photo_url: form.photo_url.trim() || null,
    quantity_wanted: Number(form.quantity_wanted),
    priority: form.priority,
  };
  if (opts.canViewFinancials) payload.price = form.price.trim() === '' ? null : normalisePrice(form.price);
  return payload;
}

/** Whether the form differs from the saved item (or from the empty form when new). */
export function giftFormChanged(form: GiftForm, saved: GiftItem | null): boolean {
  const base = saved ? giftFormFromItem(saved) : emptyGiftForm();
  return (Object.keys(base) as Array<keyof GiftForm>).some(k => base[k] !== form[k]);
}
